/**
 * Cookie consent management for GDPR/CCPA compliance.
 * Stores consent with timestamp for audit purposes.
 */

const CONSENT_STORAGE_KEY = "amanaki_cookie_consent";
const CONSENT_VERSION = 1;

export interface CookieConsentState {
  necessary: boolean; // Always true - required for site function
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface StoredConsent {
  consent: CookieConsentState;
  timestamp: string; // ISO string for audit
  version: number;
}

const DEFAULT_CONSENT: CookieConsentState = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

/**
 * Check if user has made a consent choice (banner was shown and user responded)
 */
export function hasConsentChoice(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    return !!stored;
  } catch {
    return false;
  }
}

/**
 * Get stored consent. Returns null if no choice made yet.
 */
export function getConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as StoredConsent;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Get current consent state. Returns default (all denied except necessary) if no choice.
 */
export function getConsentState(): CookieConsentState {
  const stored = getConsent();
  if (!stored) return { ...DEFAULT_CONSENT };
  return stored.consent;
}

/**
 * Save consent and update Google Consent Mode
 */
export function setConsent(consent: CookieConsentState): void {
  const stored: StoredConsent = {
    consent,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
    updateGoogleConsentMode(consent);
  } catch {
    // localStorage may be disabled
  }
}

/**
 * Update Google Consent Mode v2 (for GTM/GA)
 * Must be called after gtag is available (GTM loads it)
 */
export function updateGoogleConsentMode(consent: CookieConsentState): void {
  if (typeof window === "undefined") return;
  const gtag = (window as any).gtag;
  if (typeof gtag !== "function") return;

  const analyticsGranted =
    consent.analytics || consent.marketing ? "granted" : "denied";
  const adGranted = consent.marketing ? "granted" : "denied";

  gtag("consent", "update", {
    analytics_storage: analyticsGranted,
    ad_storage: adGranted,
    ad_user_data: adGranted,
    ad_personalization: adGranted,
  });
}

/**
 * Accept all non-essential cookies
 */
export function acceptAll(): CookieConsentState {
  const consent: CookieConsentState = {
    necessary: true,
    functional: true,
    analytics: true,
    marketing: true,
  };
  setConsent(consent);
  return consent;
}

/**
 * Reject all non-essential cookies
 */
export function rejectAll(): CookieConsentState {
  const consent: CookieConsentState = {
    ...DEFAULT_CONSENT,
  };
  setConsent(consent);
  return consent;
}
